// Presentational view of a single Tree detail (kind: "tree").
//
// Layout: hero → cover photo → two columns (timeline as the main story on the
// left; drive link, location map and QR in a sidebar on the right). On mobile
// the sidebar stacks under the timeline. No hover-only affordances.

import { Link } from "react-router-dom";
import { Sprout, MapPin, Calendar, User, ArrowLeft, ArrowRight, Leaf, AlertTriangle } from "lucide-react";
import TreeQR from "@/components/tracker/TreeQR";
import CertificateButton from "@/components/tracker/CertificateButton";
import { cldImg } from "@/lib/img";
import { formatDate } from "@/lib/date";
import LocationMap from "@/components/LocationMap";

function MetaItem({ icon: Icon, children }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <Icon className="w-4 h-4 text-seedh-green shrink-0" />
            {children}
        </span>
    );
}

export default function TreeDetailView({ data }) {
    const timeline = data.timeline ?? [];
    const isDead = data.status === "Dead";
    const isReplaced = data.status === "Replaced";
    const gone = isDead || isReplaced; // no longer a living tree at this spot

    const statusStyle = isDead
        ? "bg-red-50 text-red-700"
        : isReplaced
          ? "bg-amber-50 text-amber-700"
          : "bg-seedh-surface-green/60 text-seedh-green";

    return (
        <div className="space-y-10 md:space-y-12">
            {/* ---------- Hero ---------- */}
            <header>
                <div className="flex flex-wrap items-center gap-3 justify-between">
                    <span className="text-[0.7rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold">
                        {data.replacementOf ? "Replacement tree" : "Tracked tree"}
                    </span>
                    {data.status && (
                        <span className={`text-sm rounded-full px-3.5 py-1.5 font-medium ${statusStyle}`}>
                            {data.status}
                        </span>
                    )}
                </div>

                {/* The Tree ID is the identity — no separate name. */}
                <h1 className="font-display text-3xl md:text-4xl lg:text-[2.75rem] leading-tight mt-4 flex items-start gap-3">
                    {gone ? (
                        <AlertTriangle className="w-8 h-8 text-amber-600 shrink-0 mt-1.5" />
                    ) : (
                        <Sprout className="w-8 h-8 text-seedh-green shrink-0 mt-1.5" />
                    )}
                    <span className={`font-mono tracking-tight ${gone ? "text-seedh-muted" : "text-seedh-text"}`}>
                        {data.id}
                    </span>
                </h1>

                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm text-seedh-muted">
                    {data.species && <MetaItem icon={Leaf}>{data.species}</MetaItem>}
                    {data.location && <MetaItem icon={MapPin}>{data.location}</MetaItem>}
                    {data.plantedOn && <MetaItem icon={Calendar}>Planted {formatDate(data.plantedOn)}</MetaItem>}
                    {data.sponsor && <MetaItem icon={User}>{data.sponsor}</MetaItem>}
                </div>
            </header>

            {/* ---------- Replacement chain ----------
                Shown high on the page: if a tree was lost, say so plainly and
                point to the sapling that took its place. */}
            {(data.replacedBy || data.replacementOf || isDead) && (
                <div
                    className={`rounded-2xl border px-5 py-4 md:px-6 md:py-5 ${
                        isReplaced || isDead
                            ? "border-amber-200 bg-amber-50/60"
                            : "border-seedh-border bg-seedh-surface-green/30"
                    }`}
                >
                    {data.replacedBy ? (
                        <>
                            <p className="text-sm text-seedh-text">
                                This tree didn't survive
                                {data.replacedAt ? ` (recorded ${formatDate(String(data.replacedAt).slice(0, 10))})` : ""}
                                {data.replacementReason ? ` — ${data.replacementReason}` : ""}. We planted a
                                new one in the same spot, and it's tracked from scratch under its own ID.
                            </p>
                            <Link
                                to={`/tree/${encodeURIComponent(data.replacedBy)}`}
                                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-seedh-blue link-underline"
                            >
                                See the replacement, {data.replacedBy}
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </>
                    ) : isDead ? (
                        <p className="text-sm text-seedh-text">
                            This tree didn't survive
                            {data.deathReason ? ` — ${data.deathReason}` : ""}. The spot is empty for now;
                            a new tree will be planted here and tracked under its own ID.
                        </p>
                    ) : (
                        <>
                            <p className="text-sm text-seedh-text">
                                This sapling was planted to replace a tree that didn't survive — same
                                spot, same care.
                            </p>
                            <Link
                                to={`/tree/${encodeURIComponent(data.replacementOf)}`}
                                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-seedh-blue link-underline"
                            >
                                See the original, {data.replacementOf}
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </>
                    )}
                </div>
            )}

            {/* ---------- Cover photo ---------- */}
            {data.image && (
                <div className="overflow-hidden rounded-2xl border border-seedh-border bg-seedh-surface-beige/60 aspect-[16/9] md:aspect-[21/9]">
                    <img
                        src={cldImg(data.image, 1200)}
                        alt={data.species ? `${data.id} — ${data.species}` : data.id}
                        className="w-full h-full object-cover"
                    />
                </div>
            )}

            {/* ---------- Timeline + sidebar ---------- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-start">
                {/* Timeline — the main story */}
                <section className="lg:col-span-2 order-2 lg:order-1">
                    <h2 className="font-display text-2xl text-seedh-text">Monitoring timeline</h2>
                    <p className="text-sm text-seedh-muted mt-1.5">
                        Dated updates recorded in the field for this tree.
                    </p>

                    {timeline.length === 0 ? (
                        <div className="mt-6 rounded-2xl border border-dashed border-seedh-border bg-seedh-surface-beige/40 px-6 py-10 text-center">
                            <Sprout className="w-7 h-7 text-seedh-brown mx-auto" />
                            <p className="text-sm text-seedh-muted mt-3">
                                No updates logged yet. Check back soon.
                            </p>
                        </div>
                    ) : (
                        <ol className="mt-8 relative border-l-2 border-seedh-border ml-2 space-y-10">
                            {timeline.map((e, i) => (
                                <li key={i} className="relative pl-6 md:pl-8">
                                    <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-seedh-green ring-4 ring-white" />
                                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                        <span className="font-medium text-seedh-text">{e.title}</span>
                                        {e.date && (
                                            <span className="text-xs text-seedh-muted">{formatDate(e.date)}</span>
                                        )}
                                    </div>
                                    {e.note && (
                                        <p className="text-sm text-seedh-text/80 mt-1.5 leading-relaxed">{e.note}</p>
                                    )}
                                    {e.image && (
                                        <div className="mt-4 overflow-hidden rounded-xl border border-seedh-border bg-seedh-surface-beige/60 max-w-md aspect-[4/3]">
                                            <img
                                                src={cldImg(e.image, 800)}
                                                alt={e.title || ""}
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ol>
                    )}
                </section>

                {/* Sidebar */}
                <aside className="lg:col-span-1 order-1 lg:order-2 space-y-6">
                    <div className="card-base bg-white p-5">
                        <span className="block text-[0.7rem] tracking-[0.18em] uppercase text-seedh-brown font-semibold">
                            Certificate
                        </span>
                        <p className="text-xs text-seedh-muted mt-1.5 leading-relaxed">
                            A PDF recognising this tree, issued to {data.sponsor || "the donor"}.
                        </p>
                        <CertificateButton kind="tree" data={data} className="w-full mt-4" />
                    </div>

                    {data.drive && (
                        <Link
                            to={`/drive/${encodeURIComponent(data.drive.id)}`}
                            className="group card-base bg-seedh-surface-blue/40 p-5 flex flex-col gap-4"
                        >
                            <span>
                                <span className="block text-[0.7rem] tracking-[0.18em] uppercase text-seedh-brown font-semibold">
                                    Part of the drive
                                </span>
                                <span className="block font-medium text-seedh-text mt-1.5 leading-snug">
                                    {data.drive.title || data.drive.id}
                                </span>
                            </span>
                            <span className="btn-primary rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 w-full">
                                See full drive
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                            </span>
                        </Link>
                    )}

                    {(data.mapsQuery || data.location) && (
                        <LocationMap location={data.location} mapsUrl={data.mapsUrl} mapsQuery={data.mapsQuery} />
                    )}

                    <TreeQR
                        url={`${window.location.origin}/tree/${encodeURIComponent(data.id)}`}
                        treeId={data.id}
                    />
                </aside>
            </div>
        </div>
    );
}
